import React, { useEffect, useRef, useState } from "react";
import * as THREE from 'three';
import { useThree } from "@react-three/fiber";

import { sceneUnitsFromMm } from '../../utils/scaler';
import { ElementTypeEnum } from '../../elementTypeMappings';

import {useProductModelStore} from '../../store/productModelStore'
import { useModelPropertiesStore } from '../../store/modelPropertiesStore/modelPropertiesStore';

import { findProductFromSceneByGlobalId, getModelBoundingBoxAndDirection, findAlignedBoundingBoxByProductType } from '../../utils/geometryUtils';
import Product from "../../model/Product";
import { findFromProducts } from '../../utils/productModelUtils';
import { StoreyName } from '../StoreyNamesComponent';


// Finds stories and groups them into groups which should be handled together
// Usually these groups are buildings
const groupStories = (products : Product[]) => {
    
    const storeys = findFromProducts(products, [ElementTypeEnum.Storey]);

    // Sort storeys by floor height
    storeys.sort((a,b) => a.placement.location.z < b.placement.location.z ? -1 : 1);

    const previousZ : number | null = null;

    // Returns storeys grouped by parent and z-level
    const storeysGroupedByParent = storeys.reduce<Record<string, Record<number, Product[]>>>((acc, item) => {
        const parent = item.parent?.name ?? "no-parent";
        
        let z = item.placement.location.z;
        if (!acc[parent]) {
            acc[parent] = [];
        }
        else // Check if there is already a z value which is within 1m of this storey
        {
            const t = Object.keys(acc[parent]).map(z => Number(z));
            const maxHeight = Math.max(...t);
            z = Math.abs(maxHeight-z) < 1 ? maxHeight : z; // If storeys z-location is within 1m from another storey, it is considered to be same storey
        }

        if (!acc[parent][z]) {
            acc[parent][z] = [];
        }

        acc[parent][z].push(item);

        return acc;
    }, {})

    return storeysGroupedByParent;
};

interface AppliedTranslation {
    productGuid : string,
    originalPos: THREE.Vector3
}

const StoreysSideBySideComponent = () => {

    const { scene, size } = useThree();
    const {setBoundingBox, setAlignedBoundingBox, setRotationOffset} = useModelPropertiesStore();
    const { products } = useProductModelStore();

    const [labelNames, setLabelNames] = useState<StoreyName[]>([]);

    // Refs so the cleanup effect always has the latest values (avoids stale closure)
    const appliedTranslationsRef = useRef<AppliedTranslation[]>([]);
    const productsRef = useRef(products);
    const sizeRef = useRef(size);

    useEffect(() => { productsRef.current = products; }, [products]);
    useEffect(() => { sizeRef.current = size; }, [size]);

    const resetPositions = () => {
        appliedTranslationsRef.current.forEach(t => {
            const productObject = findProductFromSceneByGlobalId(scene, t.productGuid);
            if (productObject) {
                productObject.position.copy(t.originalPos);
                productObject.updateWorldMatrix(false, true); // propagate restored position to all children
            }
        });
        appliedTranslationsRef.current = [];
    };

    useEffect(() => {
        if (products.length === 0)
            return;

        appliedTranslationsRef.current = []; // Clear before re-applying translations

        const isCanvasHorizontal = size.width > size.height;

        // Calculate bounding box and rotation angle for this model layout
        const { boundingBox, angle } = getModelBoundingBoxAndDirection(scene, products, isCanvasHorizontal);
        
        const affectX = Math.abs(angle) < Math.PI / 4;

        // OBB local axes: localX = (cos, sin), localY = (-sin, cos).
        // Project the OBB half-size onto the translation axis to get the half-extent in world space.
        const c = Math.cos(angle);
        const s = Math.sin(angle);
        const getObbHalfExtentAlongAxis = (obb: { halfSize: THREE.Vector3 }): number => affectX
            ? Math.abs(c) * obb.halfSize.x + Math.abs(s) * obb.halfSize.y
            : Math.abs(s) * obb.halfSize.x + Math.abs(c) * obb.halfSize.y;

        // OBB center is in world space; project onto the translation axis.
        const getObbCenterAlongAxis = (obb: { center: THREE.Vector3 }): number =>
            affectX ? obb.center.x : obb.center.y;

        const groupedStories = groupStories(products);
        const margin = sceneUnitsFromMm(3000); // 3 meter margin between storeys

        // Run through each storey group (for example building)
        Object.values(groupedStories).forEach((group) =>
        {
            const storeyHeights = Object.values(group);
            // farEdge tracks the world-space far edge of the last placed storey in the translation direction.
            let farEdge = -Infinity;

            storeyHeights.forEach((sh, index) => {

                const storeyObb = findAlignedBoundingBoxByProductType(scene, sh, [ElementTypeEnum.Space], angle);
                if (storeyObb.halfSize.lengthSq() === 0) return;

                const halfExt = getObbHalfExtentAlongAxis(storeyObb);
                const centerAxis = getObbCenterAlongAxis(storeyObb);

                if (index === 0)
                {
                    // First storey stays in place; record its far edge in the translation direction.
                    farEdge = centerAxis + halfExt;
                }
                else if (isFinite(farEdge))
                {
                    // Translate so this storey's near edge sits exactly at farEdge + margin.
                    const nearEdge = centerAxis - halfExt;
                    const delta = farEdge + margin - nearEdge;

                    sh.forEach(storey => {
                        const storeyObject = findProductFromSceneByGlobalId(scene, storey.globalId) as THREE.Group;
                        if (!storeyObject) return;
                        const originalPosition = new THREE.Vector3();
                        originalPosition.copy(storeyObject.position);
                        appliedTranslationsRef.current.push({productGuid: storey.globalId, originalPos: originalPosition});
                        if (affectX) {
                            storeyObject.translateX(delta);
                        } else {
                            storeyObject.translateY(delta);
                        }
                        storeyObject.updateWorldMatrix(true, true);
                    });

                    // Advance farEdge to this storey's far edge after translation.
                    farEdge = centerAxis + delta + halfExt;
                }
            });
        });
        
        const { boundingBox: finalBoundingBox, angle: finalAngle } = getModelBoundingBoxAndDirection(scene, products, isCanvasHorizontal);

        if (!finalBoundingBox.isEmpty()){
            setBoundingBox(finalBoundingBox);
            setRotationOffset(finalAngle);
            // Compute aligned OBB once, after translations, using the final rotation angle
            const finalAlignedBoundingBox = findAlignedBoundingBoxByProductType(scene, products, [ElementTypeEnum.Space], finalAngle);
            if (finalAlignedBoundingBox.halfSize.lengthSq() > 0)
                setAlignedBoundingBox(finalAlignedBoundingBox);

            // Compute label positions now – all translations are applied and world matrices are
            // up to date (updateWorldMatrix was called on every moved storey above).
            const fc = Math.cos(finalAngle);
            const fs = Math.sin(finalAngle);
            const labelLocalY = new THREE.Vector3(-fs, fc, 0);
            const labelRotation = new THREE.Euler(0, 0, finalAngle);
            const allStoreys = findFromProducts(products, [ElementTypeEnum.Storey]);
            const labels = allStoreys.reduce<StoreyName[]>((acc, storey) => {
                const obb = findAlignedBoundingBoxByProductType(scene, [storey], [ElementTypeEnum.Space], finalAngle);
                if (obb.halfSize.lengthSq() > 0) {
                    const offset = labelLocalY.clone().multiplyScalar(-(obb.halfSize.y + 0.5));
                    const pos = obb.center.clone().add(offset);
                    acc.push({ name: storey.name, position: pos, rotation: labelRotation });
                }
                return acc;
            }, []);
            setLabelNames(labels);
        }
        
    }, [products]);

    useEffect(() => {
        return () => {
            resetPositions();

            // Recompute bounding boxes after storey positions are restored
            const latestProducts = productsRef.current;
            const latestSize = sizeRef.current;
            if (latestProducts.length > 0) {
                const isHorizontal = latestSize.width > latestSize.height;
                const { boundingBox: resetBb, angle: resetAngle } = getModelBoundingBoxAndDirection(scene, latestProducts, isHorizontal);
                if (!resetBb.isEmpty()) {
                    setBoundingBox(resetBb);
                    setRotationOffset(resetAngle);
                    const resetAlignedBb = findAlignedBoundingBoxByProductType(scene, latestProducts, [ElementTypeEnum.Space], resetAngle);
                    if (resetAlignedBb.halfSize.lengthSq() > 0)
                        setAlignedBoundingBox(resetAlignedBb);
                }
            }
        };
    }, []);

    // Show storey names when storeys are side by side
    return <>
        {/* <StoreyNamesComponent names={labelNames} /> */}
    </>
}

export default StoreysSideBySideComponent;